import { useMemo } from 'react';
import { useAppInitialLoadContext } from 'src/providers/app/AppInitialLoadProvider/AppInitialLoadProvider';
import useAccountEarnSnapshots from 'src/hooks/query/snapshots/account/useAccountEarnSnapshots';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { BigNum, QUOTE_PRECISION_EXP } from '@drift-labs/sdk';
import useAvgDepositApy from 'src/hooks/deposits/useAverageDepositApy';
import { useShallow } from 'zustand/react/shallow';
import { MAIN_POOL_ID } from '@drift/common';
import { useIsolatedPositionDepositsTotal } from 'src/hooks/useIsolatedPositionDepositsTotal';

export default function usePortfolioEarnData() {
	const { isInitialLoading } = useAppInitialLoadContext();
	const userClient = useDriftAccountStore(
		(s) => s.getCurrentUserAccount()?.client
	);
	const [currentAccountPubKey, currentAccountPoolId] = useDriftAccountStore(
		useShallow((s) => [
			s.getCurrentUserAccount()?.pubKey?.toString(),
			s.getCurrentUserAccount()?.poolId,
		])
	);
	const avgDepositApy = useAvgDepositApy({
		poolId: currentAccountPoolId ?? MAIN_POOL_ID,
	});
	const totalIsolatedDeposits = useIsolatedPositionDepositsTotal();

	const { data, isLoading, ...rest } = useAccountEarnSnapshots({
		userAccount: currentAccountPubKey,
		days: 90,
	});

	const mappedData = useMemo(() => {
		if (!data || !userClient?.isSubscribed) {
			return {
				totalDeposits: undefined,
				totalBorrows: undefined,
				total90DayEarning: undefined,
				marketIndexTo90DayEarning: undefined,
			};
		}

		const { data: earnData } = data;

		const totalDeposits = BigNum.from(
			userClient?.getSpotMarketAssetValue().add(totalIsolatedDeposits),
			QUOTE_PRECISION_EXP
		);

		const totalBorrows = BigNum.from(
			userClient?.getSpotMarketLiabilityValue(),
			QUOTE_PRECISION_EXP
		);

		let total90DayEarning = 0;
		const marketIndexTo90DayEarning = new Map<
			number,
			{ base: number; quote: number }
		>();

		try {
			for (const snapshot of earnData.snapshots) {
				for (const asset of snapshot.assets) {
					const earning =
						(asset.interestQuoteValue ?? 0) + (asset.rewards ?? 0);

					total90DayEarning += earning;

					const previousEarning = marketIndexTo90DayEarning.get(
						asset.marketIndex
					) ?? { base: 0, quote: 0 };

					const cumulativeQuoteEarning = previousEarning.quote + earning;
					const cumulativeBaseEarning =
						asset.oraclePrice > 0
							? cumulativeQuoteEarning / asset.oraclePrice
							: previousEarning.base;

					marketIndexTo90DayEarning.set(asset.marketIndex, {
						base: cumulativeBaseEarning,
						quote: cumulativeQuoteEarning,
					});
				}
			}
		} catch (error) {
			console.error('Error mapping earn data', error);
			return {
				totalDeposits,
				totalBorrows,
				total90DayEarning: BigNum.zero(QUOTE_PRECISION_EXP),
				marketIndexTo90DayEarning: new Map(),
			};
		}

		const total90DayEarningBigNum = BigNum.fromPrint(
			total90DayEarning.toString(),
			QUOTE_PRECISION_EXP
		);

		return {
			totalDeposits,
			totalBorrows,
			total90DayEarning: total90DayEarningBigNum,
			marketIndexTo90DayEarning,
		};
	}, [data, userClient, totalIsolatedDeposits]);

	const isUserClientSubscribed = userClient?.isSubscribed;

	const combinedData = useMemo(() => {
		return {
			...mappedData,
			// Return `undefined` when disconnected to show a `-` in the UI.
			avgDepositApy: isUserClientSubscribed ? avgDepositApy : undefined,
		};
	}, [mappedData, avgDepositApy, isUserClientSubscribed]);

	return {
		data: combinedData,
		isLoading: isLoading || isInitialLoading,
		...rest,
	};
}
